import React from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';

interface StatCardProps {
    title: string;
    value: string | number | null;
    change?: string | null;
    changeType?: 'positive' | 'negative' | 'neutral';
    icon: LucideIcon;
    iconColor?: string;
    iconBgColor?: string;
}

export function StatCard({
    title,
    value,
    change,
    changeType = 'neutral',
    icon: Icon,
    iconColor = 'text-primary',
    iconBgColor = 'bg-primary/10'
}: StatCardProps) {
    const changeColors = {
        positive: 'text-green-400',
        negative: 'text-red-400',
        neutral: 'text-muted-foreground'
    };

    const isLoading = value === null;

    return (
        <motion.div
            whileHover={{ y: -4, scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="stat-card glass-strong rounded-xl border border-primary/30 p-6 relative overflow-hidden"
        >
            <div className="flex items-center justify-between mb-4 relative z-10">
                <div className={`p-3 rounded-lg ${iconBgColor} border border-primary/20 transition-all duration-300 hover:scale-110`}>
                    <Icon className={`w-6 h-6 ${iconColor}`} />
                </div>
                {isLoading ? (
                    <div className="h-5 w-16 bg-muted/20 animate-pulse rounded"></div>
                ) : change && (
                    <span className={`text-sm font-semibold ${changeColors[changeType]}`}>
                        {change}
                    </span>
                )}
            </div>
            <p className="text-sm text-muted-foreground mb-2 font-medium relative z-10">{title}</p>
            {isLoading ? (
                <div className="h-8 w-20 bg-muted/20 animate-pulse rounded"></div>
            ) : (
                <h3 className="text-foreground font-bold text-2xl relative z-10">{value}</h3>
            )}
        </motion.div>
    );
}
